<template>
  <div class="sk-welcome-page">
    <div class="sk-welcome-user" v-if="$root.userEmail">
      <span class="sk-user-initials">{{ userInitials }}</span>
      <span class="sk-user-email">{{ $root.userEmail }}</span>
      <button type="button" class="sk-welcome-logout" @click="logout">Log out</button>
    </div>

    <div class="sk-welcome-main">
      <div class="sk-welcome-card">
        <!-- Sync complete banner -->
        <div v-if="effectivelyComplete" class="sk-sync-complete-banner">
          <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          <div>
            <strong>Sync complete</strong>
            <span>{{ syncCompleteSubtext }}</span>
          </div>
        </div>

        <!-- In-progress header -->
        <div v-else class="sk-welcome-header">
          <h1>Setting up your workspace&hellip;</h1>
          <p>Syncing your Amazon catalog and setting up real-time Buy Box monitoring. Takes 2–5 minutes for most accounts.</p>
        </div>

        <div v-if="error" class="sk-error-msg">{{ error }}</div>

        <!-- Checklist -->
        <div class="sk-checklist">
          <div v-for="(step, i) in derivedSteps" :key="step.id" class="sk-check-item" :class="{ done: step.completed }">
            <div class="sk-check-icon">
              <svg v-if="step.completed" viewBox="0 0 20 20" fill="#16a34a"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
              <div v-else-if="step.inProgress" class="sk-check-spinner-wrap"><div class="sk-check-spinner"></div></div>
              <div v-else class="sk-check-pending"></div>
            </div>
            <div class="sk-check-line" v-if="i < derivedSteps.length - 1"></div>
            <span class="sk-check-label">{{ step.completed ? step.completedLabel : step.label }}</span>
          </div>
        </div>

        <!-- Progress bar -->
        <div class="sk-progress-bar-wrap">
          <span class="sk-progress-left">{{ effectivelyComplete ? 'All done' : 'Sync in progress' }}</span>
          <span class="sk-progress-right" :class="{ done: effectivelyComplete }">{{ progressPercent }}%</span>
        </div>
        <div class="sk-progress-bar">
          <div class="sk-progress-fill" :class="{ done: effectivelyComplete }" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="sk-progress-label">{{ completedSteps }} of {{ totalSteps }} steps complete</div>

        <!-- CTA button -->
        <button v-if="effectivelyComplete" class="sk-btn sk-btn-done" @click="goToDashboard">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Your dashboard is ready — Go to Dashboard
        </button>
        <button v-else class="sk-btn sk-btn-progress" @click="goToDashboard">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Go to Dashboard
        </button>

        <p class="sk-footer-text">{{ effectivelyComplete ? "From tomorrow, you'll land directly on your dashboard." : 'Sync continues in the background. Your dashboard will update automatically as data comes in.' }}</p>

        <p class="sk-footer-text sk-regions-hint">Selling in more regions? Add them from <router-link to="/settings/sellers">Settings → Sellers</router-link>.</p>

        <p v-if="!effectivelyComplete" class="sk-skip-row">
          <button type="button" class="sk-skip-link" @click="skipConnectForNow">
            Skip for now — connect Amazon later
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/signup-flow.css'
import { setPipelineDoneHint, setConnectDeferredHint } from './utils/onboardingSessionHints.js'

export default {
  data: function () {
    return {
      status: {
        sellerConnected: false,
        productsImported: false,
        buyboxFetched: false,
        ordersFetched: false,
        completed: false,
        completedAt: null
      },
      error: null,
      pollTimer: null
    }
  },
  computed: {
    userInitials: function () {
      var email = this.$root.userEmail || ''
      var parts = email.split('@')[0].split(/[._-]/)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return (email[0] || 'U').toUpperCase()
    },
    derivedSteps: function () {
      var s = this.status
      return [
        { id: 'account', label: 'Amazon account connected', completedLabel: 'Amazon account connected', completed: !!s.sellerConnected, inProgress: !s.sellerConnected },
        { id: 'products', label: 'Products imported', completedLabel: 'Products imported', completed: !!s.productsImported, inProgress: !!s.sellerConnected && !s.productsImported },
        { id: 'buybox', label: 'Reading current Buy Box status', completedLabel: 'Buy Box status read', completed: !!s.buyboxFetched, inProgress: !!s.productsImported && !s.buyboxFetched },
        { id: 'sales', label: 'Fetching 30-day sales history', completedLabel: '30-day sales history fetched', completed: !!s.ordersFetched, inProgress: !!s.buyboxFetched && !s.ordersFetched }
      ]
    },
    totalSteps: function () {
      return 4
    },
    completedSteps: function () {
      var count = 0
      if (this.status.sellerConnected) count++
      if (this.status.productsImported) count++
      if (this.status.buyboxFetched) count++
      if (this.status.ordersFetched) count++
      return count
    },
    progressPercent: function () {
      return Math.round((this.completedSteps / this.totalSteps) * 100)
    },
    syncCompleteSubtext: function () {
      return 'Buy Box monitoring active · Sales history loaded'
    },
    /** All pipeline steps done (API may still report completed: false). */
    effectivelyComplete: function () {
      var s = this.status
      return !!s.completed ||
        (!!s.sellerConnected && !!s.productsImported && !!s.buyboxFetched && !!s.ordersFetched)
    }
  },
  methods: {
    fetchStatus: function () {
      var self = this
      if (self.status.completed) return
      self.$http.get('/api/v1/onboarding/status').then(
        function (response) {
          var data = response.body || {}
          self.status.sellerConnected = !!data.sellerConnected
          self.status.productsImported = !!data.productsImported
          self.status.buyboxFetched = !!data.buyboxFetched
          self.status.ordersFetched = !!data.ordersFetched
          self.status.completed = !!data.completed
          self.status.completedAt = data.completedAt || null
          self.error = null

          var allStepsDone = !!data.sellerConnected && !!data.productsImported && !!data.buyboxFetched && !!data.ordersFetched
          var pipelineDone = !!data.completed || allStepsDone
          if (pipelineDone) {
            setPipelineDoneHint()
            if (self.pollTimer) {
              clearInterval(self.pollTimer)
              self.pollTimer = null
            }
            self.$router.replace('/home')
            return
          }
        },
        function () {
          self.error = 'Unable to fetch onboarding status. Retrying…'
        }
      )
    },
    startPolling: function () {
      var self = this
      self.fetchStatus()
      if (self.status.completed) return
      self.pollTimer = setInterval(function () {
        if (self.status.completed) {
          clearInterval(self.pollTimer)
          self.pollTimer = null
          return
        }
        self.fetchStatus()
      }, 60000)
    },
    goToDashboard: function () {
      this.$router.push('/home')
    },
    skipConnectForNow: function () {
      setConnectDeferredHint()
      this.$router.push('/home')
    },
    logout: function () {
      this.$root.signOutUser()
    }
  },
  mounted: function () {
    var el = document.getElementById('preloadr')
    if (el) {
      el.style.transition = 'opacity 0.5s'
      el.style.opacity = '0'
      setTimeout(function () { el.style.display = 'none' }, 500)
    }
    this.startPolling()
  },
  beforeDestroy: function () {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }
}
</script>

<style scoped>
.sk-welcome-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  background: radial-gradient(ellipse at 50% 0%, #1e3a5f 0%, #0f2744 40%, #0b1929 100%);
  position: relative;
}

.sk-welcome-user {
  position: absolute;
  top: 20px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: rgba(255,255,255,0.08);
  border-radius: 24px;
  font-size: 13px;
  color: rgba(255,255,255,0.9);
}
.sk-welcome-logout {
  margin-left: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(0,0,0,0.15);
  color: rgba(255,255,255,0.95);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.sk-welcome-logout:hover {
  background: rgba(255,255,255,0.12);
}
.sk-user-initials {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(245,132,31,0.4);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}
.sk-user-email {
  color: rgba(255,255,255,0.75);
}

.sk-welcome-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px 40px;
}

.sk-welcome-card {
  background: rgba(26, 45, 71, 0.85);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 36px 40px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.06);
}

.sk-sync-complete-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.4);
  border-radius: 10px;
  margin-bottom: 24px;
}
.sk-sync-complete-banner svg {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}
.sk-sync-complete-banner strong {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 2px;
}
.sk-sync-complete-banner span {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
}

.sk-regions-hint { margin-top: 8px; }
.sk-regions-hint a { color: #F5841F; font-weight: 600; text-decoration: none; }
.sk-regions-hint a:hover { text-decoration: underline; }

.sk-welcome-header {
  margin-bottom: 24px;
}
.sk-welcome-header h1 {
  font-size: 20px;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 8px;
}
.sk-welcome-header p {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

.sk-error-msg {
  background: rgba(239,68,68,0.15);
  border: 1px solid rgba(239,68,68,0.4);
  color: #fca5a5;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 20px;
}

.sk-checklist {
  margin-bottom: 24px;
}
.sk-check-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  position: relative;
}
.sk-check-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sk-check-icon svg {
  width: 20px;
  height: 20px;
}
.sk-check-pending {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(148,163,184,0.5);
  border-radius: 50%;
}
.sk-check-spinner-wrap {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sk-check-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(148,163,184,0.3);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: skWelcomeSpin 0.8s linear infinite;
}
.sk-check-line {
  position: absolute;
  left: 11px;
  top: 36px;
  bottom: -4px;
  width: 2px;
  background: rgba(148,163,184,0.2);
}
.sk-check-item:last-child .sk-check-line {
  display: none;
}
.sk-check-label {
  font-size: 14px;
  color: #e2e8f0;
  font-weight: 500;
}
.sk-check-item.done .sk-check-label {
  color: #94a3b8;
}

@keyframes skWelcomeSpin {
  to { transform: rotate(360deg); }
}

.sk-progress-bar-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.sk-progress-left {
  font-size: 13px;
  color: #94a3b8;
}
.sk-progress-right {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
}
.sk-progress-right.done {
  color: #22c55e;
}
.sk-progress-bar {
  height: 8px;
  background: rgba(148,163,184,0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
}
.sk-progress-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 24px;
}
.sk-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  border-radius: 4px;
  transition: width 0.5s ease;
}
.sk-progress-fill.done {
  background: linear-gradient(90deg, #16a34a, #22c55e);
}

.sk-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}
.sk-btn-progress {
  background: #F5841F;
  color: #fff;
}
.sk-btn-progress:hover {
  background: #ea580c;
}
.sk-btn-done {
  background: #22c55e;
  color: #fff;
}
.sk-btn-done:hover {
  background: #16a34a;
}

.sk-footer-text {
  margin-top: 20px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  margin-bottom: 0;
}

.sk-skip-row {
  margin-top: 20px;
  margin-bottom: 0;
  text-align: center;
}
.sk-skip-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  font-family: inherit;
}
.sk-skip-link:hover {
  color: #cbd5e1;
}

@media (max-width: 600px) {
  .sk-welcome-card {
    padding: 28px 20px;
  }
  .sk-welcome-user {
    flex-wrap: wrap;
    justify-content: flex-end;
    max-width: calc(100vw - 32px);
    left: auto;
    right: 16px;
  }
  .sk-user-email {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>



// WEBPACK FOOTER //
// WelcomePage.vue?f19f4010